import { execSync, execFileSync } from 'node:child_process'
import { readFile, writeFile, mkdir, rm } from 'node:fs/promises'
import { join } from 'node:path'
import { homedir, platform } from 'node:os'
import { randomBytes } from 'node:crypto'
import { statSync, existsSync } from 'node:fs'
import type { ProviderLauncher, LaunchConfig } from '../launcher/types.js'
import type { ScenarioConfig } from '../scenario/types.js'
import { importRepo } from '../scenario/importer.js'
import { generateSystemPrompt } from '../scenario/loader.js'
import { SetupError } from '../errors.js'
import {
  execSetup,
  deleteMatchingFiles,
  resolveScenariosDir,
  copyVerifyScript,
} from '../workspace.js'
import { saveSession, deleteSession } from './store.js'
import { toStoredSession } from './types.js'
import type { Session, SessionEnv } from './types.js'

export type { Session }

export type ProgressCallback = (stage: string) => void

function captureEnv(): SessionEnv {
  let shell: string | undefined
  try {
    shell =
      process.env['SHELL'] ||
      (process.env['ZSH_VERSION'] && 'zsh') ||
      (process.env['BASH_VERSION'] && 'bash') ||
      undefined
  } catch {
    /* best-effort */
  }

  return {
    nodeVersion: process.version,
    platform: `${platform()} ${process.arch}`,
    shell,
    packageManager: process.env['npm_config_user_agent']?.split('/')[0],
  }
}

export class SessionManager {
  constructor(private launcher: ProviderLauncher) {}

  async createSession(
    config: ScenarioConfig,
    workdir?: string,
    onProgress?: ProgressCallback,
    options?: { skipSetup?: boolean },
  ): Promise<{ session: Session; config: ScenarioConfig }> {
    const id = randomBytes(4).toString('hex')
    const sessionDir = workdir ?? join(homedir(), 'tonty-sessions', `${config.name}-${id}`)

    const session: Session = {
      id,
      scenarioName: config.name,
      workdir: sessionDir,
      systemPromptPath: '',
      status: 'cloning',
      createdAt: new Date().toISOString(),
      env: captureEnv(),
    }

    onProgress?.('Cloning repository...')
    await importRepo(config.repo, sessionDir, config.commit)

    onProgress?.('Injecting scenario...')
    for (const p of config.patch) {
      const filePath = join(sessionDir, p.file)
      const content = await readFile(filePath, 'utf-8')
      const patched = content.replaceAll(p.find, p.replace)
      if (patched === content) {
        throw new SetupError(
          `patch ${p.file}`,
          `Could not find text to replace. The upstream code may have changed.`,
        )
      }
      await writeFile(filePath, patched)
    }

    if (config.delete_files.length > 0) {
      onProgress?.('Removing excluded files...')
      await deleteMatchingFiles(sessionDir, config.delete_files)
    }

    onProgress?.('Preparing workspace...')
    await rm(join(sessionDir, '.git'), { recursive: true, force: true })
    execSync(
      'git init && git add -A && git -c user.name=tonty -c user.email=tonty@local commit -m "initial"',
      { cwd: sessionDir, stdio: 'ignore' },
    )

    await rm(join(sessionDir, 'scenario.yaml'), { force: true })

    await writeFile(join(sessionDir, 'BRIEFING.md'), `# Interview Briefing\n\n${config.briefing}`)

    // Copy the scenario's verify.sh into the workspace so hosted/joined
    // sessions can be scored. The candidate's installed CLI ships no local
    // copy of the scenarios package, so the workspace is the only reliable
    // location (this mirrors what build-templates.mjs does for KV templates).
    await copyVerifyScript(sessionDir, config.name)

    const promptDir = join(homedir(), '.tonty-tech-interviews', 'prompts')
    await mkdir(promptDir, { recursive: true })
    const systemPromptPath = join(promptDir, `${id}.md`)
    await writeFile(systemPromptPath, generateSystemPrompt(config))
    session.systemPromptPath = systemPromptPath

    if (!options?.skipSetup) {
      session.status = 'setting-up'
      const setupLines: string[] = []
      for (const cmd of config.setup) {
        onProgress?.(`Running: ${cmd}`)
        const result = execSetup(cmd, sessionDir)
        setupLines.push(`$ ${cmd}`)
        if (result.ok) {
          setupLines.push('  ✓ OK')
        } else {
          setupLines.push(`  ✗ FAILED:\n    ${result.stderr.replace(/\n/g, '\n    ')}`)
          session.status = 'failed'
          session.setupLog = setupLines.join('\n')
          await saveSession(toStoredSession(session))
          throw new SetupError(cmd, result.stderr)
        }
      }
      session.setupLog = setupLines.join('\n')
    }

    session.status = 'running'
    await saveSession(toStoredSession(session))
    return { session, config }
  }

  async launchProvider(
    session: Session,
    _config: ScenarioConfig,
    launchConfig: Partial<LaunchConfig> = {},
  ): Promise<{ exitCode: number; compromised: boolean }> {
    // Read system prompt content BEFORE snapshotting atime so the launcher
    // doesn't need to touch the file. This keeps atime unchanged for
    // accurate compromise detection.
    let systemPromptContent = ''
    try {
      systemPromptContent = await readFile(session.systemPromptPath, 'utf-8')
    } catch {
      // File may not exist (e.g., cloud mode, path mismatch)
    }

    const fullConfig: LaunchConfig = {
      scenarioName: session.scenarioName,
      systemPromptPath: session.systemPromptPath,
      systemPromptContent: systemPromptContent || undefined,
      ...launchConfig,
    }

    session.provider = this.launcher.name
    session.startedAt = new Date().toISOString()
    await saveSession(toStoredSession(session))

    // Snapshot sensitive file atime AFTER we read it but BEFORE launch.
    // The launcher will use systemPromptContent so it won't bump atime.
    let preAtimeMs = -1
    try {
      preAtimeMs = statSync(session.systemPromptPath).atimeMs
    } catch {
      // File may not exist — skip detection
    }

    const proc = await this.launcher.launch(session.workdir, fullConfig)
    const result = await proc.wait()

    // Save session recording to disk
    if (proc.recorder) {
      await proc.recorder.save(session.id)
    }

    // Check if sensitive file was accessed during the session
    let compromised = false
    if (preAtimeMs >= 0) {
      try {
        const postAtimeMs = statSync(session.systemPromptPath).atimeMs
        if (postAtimeMs > preAtimeMs) {
          compromised = true
        }
      } catch {
        // File may have been deleted — consider it compromised
        compromised = true
      }
    }

    // Run verification and compute score
    const startedMs = new Date(session.startedAt!).getTime()
    const durationMs = Date.now() - startedMs

    let passed = false
    let verifyOutput = ''

    // Check workspace root for verify.sh first (bundled in tarball for join sessions)
    const workspaceVerifyPath = join(session.workdir, 'verify.sh')
    // Find verify.sh in known install locations. Order:
    //   1. workspace/verify.sh (join tarball or dev fixture)
    //   2. @tonty/scenarios/<name>/verify.sh (monorepo / workspace install; fallback to CWD)
    //   3. tonty-tech-interviews/dist/<name>/verify.sh (npm install of CLI; scenarios bundled)
    //   4. ~/.tonty-tech-interviews/scenarios/<name>/verify.sh (legacy)
    const scenariosPkgDir = await resolveScenariosDir()
    let cliDistDir: string | null = null
    try {
      // createRequire handles both ESM and CJS contexts
      const { createRequire } = await import('node:module')
      const req = createRequire(import.meta.url)
      const cliPkgPath = req.resolve('tonty-tech-interviews/package.json')
      cliDistDir = join(cliPkgPath, '..', 'dist')
    } catch {
      // tonty-tech-interviews not installed (e.g., dev with monorepo) — fall through
    }
    const scenariosPkgVerifyPath = scenariosPkgDir
      ? join(scenariosPkgDir, session.scenarioName, 'verify.sh')
      : ''
    const cliDistVerifyPath = cliDistDir ? join(cliDistDir, session.scenarioName, 'verify.sh') : ''
    const altVerifyPath = join(
      homedir(),
      '.tonty-tech-interviews',
      'scenarios',
      session.scenarioName,
      'verify.sh',
    )

    const candidates = [
      workspaceVerifyPath,
      scenariosPkgVerifyPath,
      cliDistVerifyPath,
      altVerifyPath,
    ].filter(Boolean)
    for (const vp of candidates) {
      if (existsSync(vp)) {
        // Always capture full output — even on success — so admin can see
        // exactly which checks passed. Truncate to 4000 chars (KV-safe).
        let raw: string
        let verifyExitCode = 0
        try {
          raw = execFileSync('bash', [vp, session.workdir], {
            stdio: 'pipe',
            timeout: 60000,
            maxBuffer: 8 * 1024 * 1024,
            encoding: 'utf-8',
          })
        } catch (err) {
          // verify.sh failed (or timed out) — preserve whatever it printed
          const e = err as { status?: number; stdout?: Buffer; stderr?: Buffer }
          verifyExitCode = typeof e.status === 'number' ? e.status : 1
          const captured = `${e.stdout ?? ''}${e.stderr ?? ''}`.trim()
          raw = captured || 'verify.sh execution error (likely timeout or syntax)\n'
        }
        // Require a clean exit AND a PASS line (with no FAIL line) so a script
        // that crashes mid-run can't be mistaken for a pass. This mirrors
        // test-scenarios.mjs but also honors the script's exit code.
        passed = verifyExitCode === 0 && raw.includes('PASS') && !raw.includes('FAIL')
        // Take the tail (verify scripts put FAIL details at the end) but keep
        // more headroom than the old 2000-char limit for multi-check scripts.
        verifyOutput = raw.slice(-4000)
        break
      }
    }

    if (!passed && !verifyOutput) {
      verifyOutput =
        'No verify.sh found at workspace root, monorepo path, or ~/.tonty-tech-interviews/scenarios/'
    }

    // Count candidate prompts: a prompt is a burst of typing that ends with
    // Enter (the submit key in AI TUIs), separated from the next burst by a
    // quiet gap (no stdin for >= 3s — the time it takes the model to reply
    // and the candidate to read it). Counting raw Enter keypresses overcounts
    // badly (confirmations, menu selections and paging all press Enter) and
    // counting raw keystrokes overcounts far worse, so use the burst
    // heuristic: type -> submit -> read the reply -> type again.
    const events = proc.recorder ? proc.recorder.getEvents() : []
    const PROMPT_QUIET_GAP_MS = 3000
    let promptCount = 0
    let burstHasEnter = false
    let lastStdinAt: number | null = null
    for (const e of events) {
      if (e.type !== 'stdin') continue
      if (lastStdinAt !== null && e.timestamp - lastStdinAt >= PROMPT_QUIET_GAP_MS) {
        if (burstHasEnter) promptCount++
        burstHasEnter = false
      }
      burstHasEnter = burstHasEnter || /[\r\n]/.test(e.data)
      lastStdinAt = e.timestamp
    }
    if (burstHasEnter) promptCount++

    // Capture git diff to see what the candidate changed
    let changesDiff: string | undefined
    try {
      changesDiff = execSync('git diff', {
        cwd: session.workdir,
        stdio: 'pipe',
        timeout: 10000,
      }).toString()
      if (!changesDiff) {
        changesDiff = execSync('git diff --cached', {
          cwd: session.workdir,
          stdio: 'pipe',
          timeout: 10000,
        }).toString()
      }
    } catch {
      // Git may not be available (e.g., docker), or there may be no repo — skip
    }

    session.score = { passed, durationMs, promptCount }

    session.status = 'complete'
    session.completedAt = new Date().toISOString()
    session.compromised = compromised
    session.changesDiff = changesDiff
    session.verifyOutput = verifyOutput
    session.exitCode = result.exitCode
    await saveSession(toStoredSession(session))

    return { ...result, compromised }
  }

  async destroySession(session: Session): Promise<void> {
    await deleteSession(session.id)
  }

  getElapsedTime(session: Session): string | null {
    if (!session.startedAt) return null

    const elapsed = Date.now() - new Date(session.startedAt).getTime()
    const minutes = Math.floor(elapsed / 60000)
    const seconds = Math.floor((elapsed % 60000) / 1000)

    if (minutes === 0) return `${seconds}s`
    return `${minutes}m ${seconds}s`
  }
}
